// Import page-specific action types from the reducer
import { PAGE_ACTION_TYPES } from '../reducers/pageReducer';
import shopifyClient from '../../clients/shopify/shopifyClient';
import { transformShopifyPages, transformShopifyPage } from '../../clients/shopify/shopifyPageTransformer';
import { getCachedData, setCachedData } from '../../utils/cacheUtils';

export const fetchPages = (cursor?: string) => async (dispatch: any) => {
  try {
    dispatch({ type: PAGE_ACTION_TYPES.SET_LOADING_STATE, isLoading: true });
    
    // Create a cache key based on the cursor
    const cacheKey = `pages_${cursor || 'initial'}`;
    
    // Try to get data from cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      console.log('Using cached pages data');
      
      dispatch({
        type: cursor ? PAGE_ACTION_TYPES.GET_MORE_PAGES : PAGE_ACTION_TYPES.FETCH_PAGES,
        payload: cachedData.pages,
        paginationInfo: cachedData.paginationInfo
      });
      
      dispatch({ type: PAGE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
      return cachedData.pages;
    }
    
    // If not in cache, fetch from API
    const response = await shopifyClient.fetchAllPages(cursor);
    const transformedPages = transformShopifyPages(response.pages);
    
    // Prepare pagination info
    const paginationInfo = {
      hasNextPage: response.pageInfo.hasNextPage,
      endCursor: response.pageInfo.endCursor,
      collectionHandle: null,
      searchTerm: null
    };
    
    // Cache the results
    setCachedData(cacheKey, {
      pages: transformedPages,
      paginationInfo
    });
    
    dispatch({
      type: cursor ? PAGE_ACTION_TYPES.GET_MORE_PAGES : PAGE_ACTION_TYPES.FETCH_PAGES,
      payload: transformedPages,
      paginationInfo
    });
    
    dispatch({ type: PAGE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
    return transformedPages;
  } catch (error) {
    console.error('Error fetching pages:', error);
    dispatch({ type: PAGE_ACTION_TYPES.SET_ERROR_STATE, error: 'Failed to fetch pages' });
    dispatch({ type: PAGE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
    return [];
  }
};

export const fetchPageByHandle = (handle: string) => async (dispatch: any) => {
  try {
    dispatch({ type: PAGE_ACTION_TYPES.SET_LOADING_STATE, isLoading: true });
    
    // Create a cache key for this specific page
    const cacheKey = `page_${handle}`;
    
    // Try to get data from cache first
    const cachedPage = getCachedData(cacheKey);
    if (cachedPage) {
      console.log(`Using cached page data for: ${handle}`);
      
      dispatch({
        type: PAGE_ACTION_TYPES.FETCH_PAGE,
        payload: cachedPage
      });
      
      dispatch({ type: PAGE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
      return cachedPage;
    }
    
    // If not in cache, fetch from API
    const page = await shopifyClient.fetchPageByHandle(handle);
    const transformedPage = page ? transformShopifyPage(page) : null;
    
    if (transformedPage) {
      // Cache the page
      setCachedData(cacheKey, transformedPage);
      
      dispatch({
        type: PAGE_ACTION_TYPES.FETCH_PAGE,
        payload: transformedPage
      });
    }
    
    dispatch({ type: PAGE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
    return transformedPage;
  } catch (error) {
    console.error('Error fetching page by handle:', error);
    dispatch({ type: PAGE_ACTION_TYPES.SET_ERROR_STATE, error: `Failed to fetch page: ${handle}` });
    dispatch({ type: PAGE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
    return null;
  }
};
